import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Download, PlugZap, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiError } from "@/lib/api";
import { amount, dateLabel, download, periodPresets } from "@/lib/books";

/** Page title row: breadcrumb-ish kicker, title, subtitle, actions on the right. */
export function PageHeader({ kicker, title, subtitle, children }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {kicker && <div className="text-xs font-medium text-slate-500">{kicker}</div>}
        <h1 className="truncate text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">{title}</h1>
        {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

/** What every Books page shows before a Tally company is connected. */
export function NotConnected() {
  return (
    <div className="flex max-w-xl flex-col items-start gap-3 rounded-lg border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-8">
      <PlugZap size={26} className="text-[#408dfb]" />
      <div className="text-lg font-semibold">Connect Tally to see this</div>
      <p className="text-sm text-slate-600">
        Your ledgers, vouchers and reports come straight from your Tally company.
      </p>
      <Link to="/connect"><Button className="bg-[#408dfb] hover:bg-[#2f7bea]">Connect Tally</Button></Link>
    </div>
  );
}

/** Loading / error / not-connected, in one place. Renders children when there is data. */
export function Loaded({ state, children }) {
  if (state.notConnected) return <NotConnected />;
  if (state.error && !state.data)
    return <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{state.error}</div>;
  if (!state.data)
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  // While refreshing, the previous figures stay, dimmed — no flash.
  return <div className={state.loading ? "opacity-60 transition-opacity" : "transition-opacity"}>{children}</div>;
}

function useOutside(ref, onOutside) {
  useEffect(() => {
    const handler = (e) => ref.current && !ref.current.contains(e.target) && onOutside();
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside]);
}

/**
 * Date range filter: preset rows, custom range underneath. Calls
 * onChange({from, to}) when a preset is picked or a custom range applied.
 */
export function PeriodFilter({ value, onChange, fyMonth = 4 }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(value);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const presets = periodPresets(fyMonth);
  const current = presets.find((p) => p.from === value.from && p.to === value.to);
  useEffect(() => setCustom(value), [value]);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="period-filter"
        className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400">
        <span className="text-slate-500">Date range:</span>
        <span className="font-medium">{current ? current.label : `${dateLabel(value.from)} – ${dateLabel(value.to)}`}</span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {presets.map((p) => (
            <button key={p.key} onClick={() => { onChange({ from: p.from, to: p.to }); setOpen(false); }}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-slate-50">
              {p.label}
              {current?.key === p.key && <Check size={16} strokeWidth={3} className="text-blue-600" />}
            </button>
          ))}
          <div className="mt-1 space-y-2 border-t border-slate-100 px-3 pb-2 pt-2">
            <div className="text-xs font-medium text-slate-500">Custom range</div>
            <div className="flex items-center gap-2">
              <Input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="h-8 text-xs" />
              <Input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="h-8 text-xs" />
            </div>
            <Button size="sm" className="w-full bg-[#408dfb] hover:bg-[#2f7bea]" disabled={!custom.from || !custom.to || custom.from > custom.to}
              onClick={() => { onChange(custom); setOpen(false); }}>Apply</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** "As of" date for point-in-time reports. */
export function AsOfFilter({ value, onChange }) {
  return (
    <label className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm">
      <span className="text-slate-500">As of</span>
      <input type="date" value={value} onChange={(e) => e.target.value && onChange(e.target.value)}
        className="bg-transparent text-sm font-medium outline-none" data-testid="as-of" />
    </label>
  );
}

/** Export dropdown: PDF / Excel / CSV of the same endpoint with the same filters. */
export function ExportMenu({ path, params }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const go = async (fmt) => {
    setBusy(fmt);
    try {
      await download(path, params, fmt);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(null);
    setOpen(false);
  };
  return (
    <div className="relative" ref={ref}>
      <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)} className="gap-1.5" data-testid="export-menu">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export <ChevronDown size={13} />
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-36 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {[["pdf", "PDF"], ["xlsx", "Excel (XLSX)"], ["csv", "CSV"]].map(([fmt, label]) => (
            <button key={fmt} disabled={busy !== null} onClick={() => go(fmt)}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50">{label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/** A right-aligned money cell. `dash` shows "—" for zero. */
export function Money({ value, dash = false, className = "" }) {
  const n = Number(value || 0);
  return (
    <span className={`tabular-nums ${n < 0 ? "text-slate-900" : ""} ${className}`}>
      {dash && Math.abs(n) < 0.005 ? "—" : amount(n)}
    </span>
  );
}

/**
 * A plain table in the Zoho style: uppercase grey header, hairline rows.
 * columns: [{key, label, align, render(row), className}]
 */
export function DataTable({ columns, rows, onRowClick, empty = "Nothing to show for this period.", footer, rowKey }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/70">
            {columns.map((c) => (
              <th key={c.key} className={`whitespace-nowrap px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${
                c.align === "right" ? "text-right" : "text-left"} ${c.className || ""}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-slate-500">{empty}</td></tr>
          )}
          {rows.map((row, i) => (
            <tr key={rowKey ? rowKey(row) : row.guid || i} onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`border-b border-slate-100 last:border-0 ${onRowClick ? "cursor-pointer hover:bg-blue-50/40" : ""}`}>
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-2.5 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.className || ""}`}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot className="border-t border-slate-200 bg-slate-50/70 font-semibold">{footer}</tfoot>}
      </table>
    </div>
  );
}

/** Invoice / bill status as Zoho shows it: coloured word, never colour alone. */
export function StatusText({ status }) {
  const map = {
    PAID: ["Paid", "text-emerald-700"],
    OVERDUE: ["Overdue", "text-orange-600"],
    PARTIALLY_PAID: ["Partially paid", "text-blue-700"],
    UNPAID: ["Unpaid", "text-slate-600"],
    NO_BILL: ["—", "text-slate-400"],
  };
  const [label, cls] = map[status] || [null, ""];
  return label ? <span className={`text-xs font-semibold uppercase tracking-wide ${cls}`}>{label}</span> : null;
}

/** Page-level tabs (Overview / Transactions / Statement). */
export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-5 border-b border-slate-200">
      {tabs.map(([key, label]) => (
        <button key={key} onClick={() => onChange(key)} data-testid={`tab-${key}`}
          className={`-mb-px border-b-2 pb-2 pt-1 text-sm ${value === key
            ? "border-[#408dfb] font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

/** A small "Page x of y" pager. */
export function Pager({ page, pageSize, total, onChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="mt-3 flex items-center justify-end gap-2 text-sm text-slate-600">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</Button>
      <span>Page {page} of {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>Next</Button>
    </div>
  );
}
